// src/components/CartPage.jsx

import { useCart, cartLineRefForUpdate } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import BackButton from "./BackButton";
import visa from "../images/visa.png";
import mastercard from "../images/mastercard.jpg";
import cash from "../images/cash.png";

export default function CartPage() {
  const { cartItems, removeFromCart, updateQuantity, totalPrice } = useCart();
  const navigate = useNavigate();

  // ✅ Utility to safely format price
  const formatPrice = (price) => {
    const num = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  const unitCount = cartItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 1),
    0
  );

  if (cartItems.length === 0) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
        <BackButton />
        <div className="ob-empty">
          <div className="ob-empty-glyph">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-6 h-6"
            >
              <path d="M4 8h16l-1.3 12.2a2 2 0 0 1-2 1.8H7.3a2 2 0 0 1-2-1.8Z" />
              <path d="M9 8V6a3 3 0 0 1 6 0v2" />
            </svg>
          </div>
          <div className="ob-display text-[27px]" style={{ color: "var(--ob-ink)" }}>
            Your bag is empty
          </div>
          <p className="text-[13px] mt-2 max-w-[340px] mx-auto leading-relaxed">
            Browse the edit, or just say{" "}
            <b style={{ color: "var(--ob-ink-2)" }}>
              &ldquo;add the second one to my cart&rdquo;
            </b>{" "}
            and Orbiee will do it for you.
          </p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="ob-btn ob-btn-solid ob-btn-lg mt-6"
          >
            Continue shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
      <BackButton />

      <div className="flex items-end justify-between gap-4 mb-6 mt-2">
        <div>
          <span className="ob-eyebrow">Shopping bag</span>
          <h1 className="ob-display text-[clamp(1.8rem,4.2vw,2.5rem)] leading-[1.08] mt-1">
            Your bag
          </h1>
        </div>
        <span className="ob-chip shrink-0">
          {unitCount} item{unitCount === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid lg:grid-cols-[1fr,352px] gap-6 items-start">
        {/* ── Lines ── */}
        <div className="flex flex-col gap-2.5 ob-stagger">
          {cartItems.map((item, idx) => {
            const qty = Number(item.quantity) || 1;
            return (
              <div
                key={item.cartLineUid ?? `${idx}-${item.id}-${item.size ?? ""}`}
                className="ob-row"
              >
                <img
                  src={item.image_uri || item.image}
                  alt={item.name || item.title || ""}
                  className="ob-row-img"
                  loading="lazy"
                  decoding="async"
                  onClick={() => item.id && navigate(`/item/${item.id}`)}
                />

                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold leading-snug ob-ink">
                    {item.name || item.title}
                  </div>

                  <div className="text-[11px] ob-ink3 mt-1">
                    {item.size ? `Size ${item.size}` : "One size"}
                    {qty > 1 && (
                      <>
                        {" · "}
                        {`USD ${formatPrice(item.price)} each`}
                      </>
                    )}
                  </div>

                  <div className="mt-2.5 flex items-center gap-2">
                    <span className="ob-qty">
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            cartLineRefForUpdate(item),
                            item.quantity - 1
                          )
                        }
                        aria-label="Decrease quantity"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          className="w-3.5 h-3.5"
                        >
                          <path d="M5 12h14" />
                        </svg>
                      </button>
                      <span>{qty}</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            cartLineRefForUpdate(item),
                            item.quantity + 1
                          )
                        }
                        aria-label="Increase quantity"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          className="w-3.5 h-3.5"
                        >
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      </button>
                    </span>

                    <button
                      type="button"
                      onClick={() => removeFromCart(cartLineRefForUpdate(item))}
                      className="ob-btn ob-btn-quiet ob-btn-sm ob-ink3"
                      title="Remove"
                      aria-label="Remove from bag"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.75}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-3.5 h-3.5"
                      >
                        <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v5M14 11v5" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <div className="text-[14px] font-bold ob-tabnum ob-ink">
                    USD {formatPrice(item.price * qty)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Summary ── */}
        <aside className="ob-panel p-5 lg:sticky" style={{ top: "calc(var(--ob-topbar-h) + 16px)" }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[13px] font-semibold ob-ink">Summary</span>
            <button
              type="button"
              className="text-[11px] font-semibold ob-underline"
              style={{ color: "var(--ob-agent)" }}
            >
              Add a discount
            </button>
          </div>

          <div className="flex justify-between text-[12.5px] ob-ink2 py-1.5">
            <span>Order value</span>
            <span className="font-semibold ob-tabnum ob-ink">
              USD {formatPrice(totalPrice)}
            </span>
          </div>
          <div className="flex justify-between text-[12.5px] ob-ink2 py-1.5">
            <span>Estimated delivery</span>
            <span className="font-semibold" style={{ color: "var(--ob-ok)" }}>
              Free
            </span>
          </div>

          <div className="flex justify-between items-baseline text-[16px] font-bold pt-3 mt-2 ob-hair-t ob-ink">
            <span>Total</span>
            <span className="ob-tabnum">USD {formatPrice(totalPrice)}</span>
          </div>

          <button
            type="button"
            onClick={() => navigate("/checkout")}
            className="ob-btn ob-btn-solid ob-btn-lg ob-btn-block mt-4"
          >
            Continue to checkout
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.9}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[15px] h-[15px]"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>

          <div className="mt-4 flex items-center gap-2.5 opacity-80">
            <img src={visa} alt="Visa" className="h-5 rounded" />
            <img src={mastercard} alt="Mastercard" className="h-5 rounded" />
            <img src={cash} alt="Cash on Delivery" className="h-5 rounded" />
          </div>

          <div className="mt-5 pt-4 ob-hair-t space-y-2.5">
            <div className="flex items-start gap-2 text-[11.5px] ob-ink2 leading-relaxed">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.7}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-[14px] h-[14px] shrink-0 mt-[2px]"
                style={{ color: "var(--ob-ok)" }}
              >
                <path d="M3 12a9 9 0 1 0 3-6.7M3 4v4h4" />
              </svg>
              <span>15 days free returns on everything.</span>
            </div>
            <div className="flex items-start gap-2 text-[11.5px] ob-ink2 leading-relaxed">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.7}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-[14px] h-[14px] shrink-0 mt-[2px]"
                style={{ color: "var(--ob-ok)" }}
              >
                <rect x="4" y="10" width="16" height="10" rx="2.5" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
              <span>
                Prices and delivery costs are confirmed at checkout. Payment is
                handled securely.
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate("/checkout")}
            className="ob-btn ob-btn-quiet ob-btn-sm ob-btn-block mt-3 ob-ink3"
          >
            Delivery and return options
          </button>
        </aside>
      </div>
    </div>
  );
}
